import type { RenewalFormInfo } from '../containers/GenerateRenewalOffer/types';
import { getCookie } from '../utils/cookie';
import request, { apiURL, requestToDownload } from '../utils/api';
class RenewalOfferService {
  getAllLeaseTerms(organizationId: string, propertyId: string) {
    const options = {
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };
    return request(
      `${apiURL}/${organizationId}/${propertyId}/lease-terms`,
      options,
    );
  }

  getAllPreviousRenewalOffers(
    organizationId: string,
    propertyId: string,
    leaseId: string,
  ) {
    const options = {
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };
    return request(
      `${apiURL}/${organizationId}/${propertyId}/lease/${leaseId}/lease-renewal`,
      options,
    );
  }

  /**
   * ENG-30334 — what REBA says about each lease term this property offers, for this lease's unit.
   *
   * Read-only, and only ever a hint: the API re-resolves pricing on save and overwrites any tier
   * REBA owns, so a stale or tampered value from this response cannot become the saved rent.
   * Returns `{ hasPricing: false, prices: [] }` — never an error — when the `reba-rev-mgmt` flag
   * is off or pricing is unavailable, which the form reads as "leave every field editable".
   *
   * Response shape: `{ hasPricing: boolean, prices: Array<{ termId, termMonths, amount,
   *   isAuthoritative, provider: { id: string, name: string } | null }> }`. `provider` lives on
   * each entry, not the top-level response, and can be `null` even when that entry is
   * `isAuthoritative: true`.
   */
  getRenewalPricing(
    organizationId: string,
    propertyId: string,
    leaseId: string,
  ) {
    const options = {
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };
    return request(
      `${apiURL}/api/v2/${organizationId}/${propertyId}/lease/${leaseId}/renewal-pricing`,
      options,
    );
  }

  createOffer(
    organizationId: string,
    propertyId: string,
    offer: RenewalFormInfo,
    leaseId: string,
  ) {
    const options = {
      method: 'POST',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(offer),
    };
    return request(
      `${apiURL}/${organizationId}/${propertyId}/lease/${leaseId}/lease-renewal`,
      options,
    );
  }

  downloadOffer(leaseId: string) {
    const options = {
      headers: {
        Authorization: getCookie('session_id'),
      },
    };

    return requestToDownload(
      `${apiURL}/lease/${leaseId}/generateLeaseOffer`,
      options,
    );
  }
}

export default RenewalOfferService;
